/**
 * MuiMaterialsList - Optimized Materials List Page
 *
 * OPTIMIZATION: Phase 4.1 - Server-side pagination implementation
 *
 * Changes made:
 * - Replaced getMaterials() with usePaginatedData(getMaterialsPaginated) hook
 * - Added debounced search (300ms) - server-side text search across name, sku, category
 * - Server-side pagination with limit/offset
 * - Server-side sorting by name ASC (default)
 * - DataGrid now uses paginationMode="server" and sortingMode="server"
 *
 * Benefits:
 * - Only loads visible page data (e.g., 25 records instead of 1000+)
 * - Search queries happen on server, reducing client memory usage
 * - Faster initial load and navigation between pages
 */
import React, { useMemo, useCallback } from "react"
import PropTypes from "prop-types"
import { useNavigate } from "react-router-dom"
import {
  Box,
  Container,
  CircularProgress,
  Typography,
  TextField,
  InputAdornment,
} from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import SearchIcon from "@mui/icons-material/Search"
import AddIcon from "@mui/icons-material/Add"
import { MuiPageHeader, MuiLoadingButton, MuiCard } from "../../components/mui"
import { getMaterialsPaginated } from "../../helpers/api_helper"
import { usePaginatedData } from "../../hooks/usePaginatedData"

const MuiMaterialsList = () => {
  const navigate = useNavigate()
  document.title = "Materials List | FlexFlow ERP"

  // ============================================================================
  // SERVER-SIDE PAGINATION via usePaginatedData hook
  // ============================================================================
  const {
    data: materials,
    total,
    loading,
    error,
    pagination,
    searchTerm,
    sortModel,
    handlePageChange,
    handlePageSizeChange,
    handleSearchChange,
    handleSortChange,
    refresh
  } = usePaginatedData(getMaterialsPaginated, {
    initialPageSize: 25,
    initialSort: { field: "name", direction: "ASC" },
    debounceMs: 300,
    autoFetch: true
  })

  // Define columns for DataGrid
  const columns = useMemo(
    () => [
      {
        field: "sku",
        headerName: "SKU",
        flex: 0.8,
        minWidth: 100,
      },
      {
        field: "name",
        headerName: "Name",
        flex: 1.5,
        minWidth: 180,
      },
      {
        field: "category",
        headerName: "Category",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "costprice",
        headerName: "Cost Price",
        flex: 0.8,
        minWidth: 100,
        valueFormatter: (value) => {
          if (value == null) return ""
          return `${Number(value).toFixed(2)}`
        },
      },
      {
        field: "stock",
        headerName: "Stock",
        flex: 0.6,
        minWidth: 80,
        type: "number",
      },
    ],
    []
  )

  // Handle row click
  const handleRowClick = useCallback((params) => {
    navigate(`/mui-material-overview/${params.row.id}`)
  }, [navigate])

  // Handle add material
  const handleAddMaterial = useCallback(() => {
    navigate("/mui-create-material")
  }, [navigate])

  // Handle DataGrid pagination model change
  const handlePaginationModelChange = useCallback((model) => {
    if (model.pageSize !== pagination.pageSize) {
      handlePageSizeChange(model.pageSize)
    }
    if (model.page !== pagination.page) {
      handlePageChange(model.page)
    }
  }, [pagination.page, pagination.pageSize, handlePageChange, handlePageSizeChange])

  // Handle DataGrid sort model change
  const handleSortModelChange = useCallback((model) => {
    if (model.length > 0) {
      const { field, sort } = model[0]
      handleSortChange(field, sort === "asc" ? "ASC" : "DESC")
    }
  }, [handleSortChange])

  // Convert sort model for DataGrid format
  const dataGridSortModel = useMemo(() => {
    if (sortModel.field) {
      return [{ field: sortModel.field, sort: sortModel.direction.toLowerCase() }]
    }
    return [{ field: "name", sort: "asc" }]
  }, [sortModel])

  return (
    <Box sx={{ py: 3 }}>
      <Container maxWidth={false}>
        <MuiPageHeader
          title="Materials"
          breadcrumbs={[
            { label: "Materials", path: "/mui-materials-list" },
            { label: "List" },
          ]}
          actions={
            <MuiLoadingButton
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddMaterial}
            >
              Add Material
            </MuiLoadingButton>
          }
        />

        <MuiCard noPadding>
          {/* Search Bar */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
            <TextField
              size="small"
              placeholder="Search by name, SKU, or category..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 300 }}
            />
            <Typography
              component="span"
              sx={{ ml: 2, fontSize: "0.875rem", color: "text.secondary" }}
            >
              {total} results
            </Typography>
          </Box>

          {/* DataGrid */}
          <Box sx={{ height: 600, width: "100%" }}>
            {loading && materials.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <CircularProgress />
              </Box>
            ) : materials.length === 0 && !loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <Typography color="text.secondary">
                  {searchTerm
                    ? "No materials found matching your search"
                    : "No materials found"}
                </Typography>
              </Box>
            ) : (
              <DataGrid
                rows={materials}
                columns={columns}
                getRowId={(row) => row.id}
                loading={loading}
                // Server-side pagination
                paginationMode="server"
                rowCount={total}
                paginationModel={{
                  page: pagination.page,
                  pageSize: pagination.pageSize,
                }}
                onPaginationModelChange={handlePaginationModelChange}
                pageSizeOptions={[10, 25, 50, 100]}
                // Server-side sorting
                sortingMode="server"
                sortModel={dataGridSortModel}
                onSortModelChange={handleSortModelChange}
                // Other settings
                disableRowSelectionOnClick
                onRowClick={handleRowClick}
                sx={{
                  border: 0,
                  "& .MuiDataGrid-row": {
                    cursor: "pointer",
                  },
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "action.hover",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "grey.50",
                  },
                }}
              />
            )}
          </Box>
        </MuiCard>
      </Container>
    </Box>
  )
}

MuiMaterialsList.propTypes = {
  materials: PropTypes.array,
}

export default MuiMaterialsList
